import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  BuildingOfficeIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';

import { countries } from '../utils/countries';

const API_URL = '/api/v1';

export default function KYC() {
  const [searchParams] = useSearchParams();
  const registrationType = searchParams.get('type') || 'PRODUCER';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '',
    registrationNumber: '',
    vatNumber: '',
    country: 'MA',
    email: '',
    phone: '',
    legalRepName: '',
    orgType: 'CORPORATION',
    termsAccepted: false,
    docTax: '',
    docRc: '',
    docStatutes: ''
  });

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    if (!files || !files[0]) return;

    const file = files[0];
    const formPayload = new FormData();
    formPayload.append('file', file);

    try {
      const res = await axios.post('/api/v1/uploads', formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Store the real serving URL, not just the filename
      setFormData(prev => ({ ...prev, [name]: res.data.url }));
    } catch (err) {
      alert(`File upload failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.termsAccepted) {
      setError('You must accept the Standard Terms and Conditions (Section E7).');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        account_number: formData.registrationNumber,
        account_name: formData.companyName,
        account_type: registrationType,
        email: formData.email,
        country_code: formData.country,
        registry_code: 'MAPGO',
        phone: formData.phone,
        vat_number: formData.vatNumber,
        legal_representative: formData.legalRepName,
        organization_type: formData.orgType,
        standard_terms_accepted: formData.termsAccepted,
        doc_tax_certificate: formData.docTax,
        doc_rc: formData.docRc,
        doc_statutes: formData.docStatutes
      };

      await axios.post(`${API_URL}/accounts`, payload);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please check your data.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-xl mx-auto py-20 text-center animate-fade-in px-4">
        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
          <CheckCircleIcon className="w-12 h-12 text-emerald-500" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4 tracking-tight">Application Submitted</h2>
        <p className="text-gray-400 mb-8 leading-relaxed">
          Your documentation has been queued for review by the International Registry.
          You will receive an activation status update via email within 48 business hours.
        </p>
        <button onClick={() => window.location.href = '/'} className="bg-emerald-500 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-600">
          Return to Hub
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-12 px-4">
      <div className="mb-12 text-center">
        <h1 className="text-4xl font-black text-gray-900 mb-2 tracking-tighter">Registry Registration</h1>
        <p className="text-gray-500 font-medium">EECS Compliance Onboarding Portal</p>
      </div>

      {error && (
        <div className="mb-8 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 flex items-center gap-3 animate-shake">
          <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-bold">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8 animate-fade-in">
        <div className="glass-card p-8 rounded-[32px]">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-emerald-500/10 p-2 rounded-lg">
              <BuildingOfficeIcon className="w-6 h-6 text-emerald-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">Entity Profile</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Company Legal Name</label>
              <input required name="companyName" value={formData.companyName} onChange={handleChange} type="text" className="full-input" placeholder="Legal name as per registry" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Business Registration ID</label>
              <input required name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} type="text" className="full-input font-mono" placeholder="Internal or State ID" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">International VAT / Tax ID</label>
              <input required name="vatNumber" value={formData.vatNumber} onChange={handleChange} type="text" className="full-input font-mono" placeholder="e.g. EU123456789" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Entity Classification</label>
              <select name="orgType" value={formData.orgType} onChange={handleChange} className="full-input select-arrow">
                <option value="CORPORATION">Private Corporation</option>
                <option value="LLC">Limited Liability Company</option>
                <option value="PUBLIC">Public / Governmental Authority</option>
                <option value="NGO">Non-Profit Organization</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Registry Domain (ISO Country)</label>
              <select name="country" value={formData.country} onChange={handleChange} className="full-input select-arrow">
                <option value="EU">EU Sector (Regional)</option>
                {countries.map(c => (
                  <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="glass-card p-8 rounded-[32px]">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-emerald-500/10 p-2 rounded-lg">
              <UserIcon className="w-6 h-6 text-emerald-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">Legal Representative</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2 space-y-2">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Authorized Representative Name</label>
              <input required name="legalRepName" value={formData.legalRepName} onChange={handleChange} type="text" className="full-input" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Official Email Address</label>
              <input required name="email" value={formData.email} onChange={handleChange} type="email" className="full-input" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Primary Contact Phone</label>
              <input required name="phone" value={formData.phone} onChange={handleChange} type="tel" className="full-input" />
            </div>
          </div>
        </div>

        {registrationType === 'VVB_AUDITOR' && (
          <div className="glass-card p-8 rounded-[32px] border-indigo-200 bg-indigo-50/10">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-indigo-500/10 p-2 rounded-lg">
                <DocumentArrowUpIcon className="w-6 h-6 text-indigo-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">Accreditation Evidence</h3>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl">
                <div>
                  <p className="text-xs font-black uppercase text-gray-700">Tax Certificate (Attestation Fiscale)</p>
                  <p className="text-[9px] text-gray-400 font-bold uppercase">Required for VVB Licensing</p>
                </div>
                <input required type="file" name="docTax" onChange={handleFileChange} className="text-[10px] text-gray-500" />
              </div>

              <div className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl">
                <div>
                  <p className="text-xs font-black uppercase text-gray-700">Registration Certificate (RC)</p>
                  <p className="text-[9px] text-gray-400 font-bold uppercase">Commercial Registry Extract</p>
                </div>
                <input required type="file" name="docRc" onChange={handleFileChange} className="text-[10px] text-gray-500" />
              </div>

              <div className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl">
                <div>
                  <p className="text-xs font-black uppercase text-gray-700">Company Statutes</p>
                  <p className="text-[9px] text-gray-400 font-bold uppercase">Verified Governance Documents</p>
                </div>
                <input required type="file" name="docStatutes" onChange={handleFileChange} className="text-[10px] text-gray-500" />
              </div>
            </div>
          </div>
        )}

        <div className="glass-card p-8 rounded-[32px] border-emerald-500/20 bg-emerald-500/5">
          <div className="flex items-start gap-4">
            <input
              required
              type="checkbox"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleChange}
              className="mt-1 w-6 h-6 rounded border-white/10 bg-white/5 text-emerald-500 focus:ring-emerald-500 cursor-pointer"
            />
            <div>
              <p className="text-sm font-bold text-gray-900 uppercase tracking-tight">Accept Standard Terms and Conditions (Section E7)</p>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                By checking this box, the Account Holder agrees to the standard terms of the MAP GO Registry and the EECS/AIB international rules. This includes consent for audit, data sharing with the AIB Hub, and verification of meter readings.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-14 py-5 rounded-2xl font-black shadow-xl shadow-emerald-500/20 disabled:opacity-50 transition-all hover:-translate-y-1"
          >
            {loading ? 'Transmitting Data...' : 'Submit Onboarding Application'}
          </button>
        </div>
      </form>
    </div>
  );
}
